import { useEffect, useMemo, useState } from "react";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { useTranslation } from 'react-i18next';
import { Group } from "../../models/interfaces/group";
import { Tag } from "../../models/interfaces/tag";
import ReportDataUtil from "../../utilities/report-data-util";
import { FilterState } from "../../models/interfaces/filter-state";
import { ReportDynamicWellnessSummaryData } from "../../models/interfaces/reports/report-dynamic-wellness-summary-data";
import { ReportWellnessSummaryUserData } from "../../models/interfaces/reports/report-wellness-summary-user-data";
import { WellnessProgramStatus, WellnessProgramStatusTranslation } from "../../models/interfaces/wellness-program-status";
import { DateTime } from "luxon";
import { Button } from "../button/button";
import ReportDynamicTable, { TableColumn } from "./report-dynamic-table";
import { ReportFilterDefinition } from "../../models/interfaces/report-filter-definition";
import { ReportFilterType } from "../../models/enumerations/report-filter-type";
import ReportFilterMenu from "../report-filter-menu/report-filter-menu";
import { Loader } from "../loader/loader";

const PAGE_SIZE = 25;
const COMPONENT_CLASS = "c-report-wellness-summary-dynamic";

const DEFAULT_FILTERS: FilterState = {
    groupIds: [],
    tagIds: [],
};


// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ReportWellnessSummaryDynamic: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const { t } = useTranslation();
    const { state } = useAuthState();
    const [reportData, setReportData] = useState<ReportDynamicWellnessSummaryData | null>(null);
    const [filteredReportData, setFilteredReportData] = useState<ReportDynamicWellnessSummaryData | null>(null);
    const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [groups, setGroups] = useState<Group[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);

    const columns = useMemo<TableColumn<ReportWellnessSummaryUserData>[]>(() => [
        {
            key: "name",
            header: t('reports.compliance.name'),
            sortable: true,
            width: 240,
            fontWeight: 400,
            render: row => (
                <a
                    href={`${window.location.origin}/users/${row.id}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {row.name}
                </a>
            )
        },
        {
            key: "programStatus",
            header: t('reports.wellnessSummary.table.programStatus'),
            sortable: true,
            render: row => (
                <span
                    className={
                        row.programStatus === WellnessProgramStatus.Pending
                            ? "status-enrolled"
                            : row.programStatus === WellnessProgramStatus.Ended
                                ? "status-retest"
                                : row.programStatus === WellnessProgramStatus.Active
                                    ? "status-active"
                                    : ""
                    }
                >
                    {row.programStatus != null
                        ? t(WellnessProgramStatusTranslation[row.programStatus])
                        : ""}
                </span>
            ),
            sortAccessor: row => row.programStatus ?? "",
            width: 120
        },
        {
            key: "daysLeft",
            header: t('reports.wellnessSummary.table.daysLeft'),
            sortable: true,
            sortAccessor: row => row.daysLeft ?? 0,
            width: 100
        },
        {
            key: "daysMissed",
            header: t('reports.wellnessSummary.table.daysMissed'),
            sortable: true,
            sortAccessor: row => row.daysMissed ?? 0,
            width: 120
        },
        {
            key: "currentStreak",
            header: t('reports.wellnessSummary.table.currentStreak'),
            sortable: true,
            sortAccessor: row => row.currentStreak ?? 0,
            width: 80
        },
        {
            key: "sevenDayEngagement",
            header: t('reports.wellnessSummary.table.sevenDayEngagement'),
            sortable: true,
            render: row => (
                <span className={row.sevenDayEngagement < 40 ? "low-engagement" : ""}>
                    {`${row.sevenDayEngagement}%`}
                </span>
            ),
            sortAccessor: row => row.sevenDayEngagement ?? 0,
            width: 100
        },
        {
            key: "thirtyDayEngagement",
            header: t('reports.wellnessSummary.table.thirtyDayEngagement'),
            sortable: true,
            render: row => (
                <span className={row.thirtyDayEngagement < 50 ? "low-engagement" : ""}>
                    {`${row.thirtyDayEngagement}%`}
                </span>
            ),
            sortAccessor: row => row.thirtyDayEngagement ?? 0,
            width: 120
        },
        {
            key: "progressPercentage",
            header: t('reports.wellnessSummary.table.progress'),
            sortable: true,
            render: row => (
                <span className="font-bold">
                    {`${row.progressPercentage}%`}
                </span>
            ),
            sortAccessor: row => row.progressPercentage ?? 0,
            width: 120
        }
    ], [t]);


    const filterDefinitions: ReportFilterDefinition[] = useMemo(() => [
        {
            key: "groupIds",
            label: t('reports.filterOptions.groups'),
            placeholder: t('reports.filterOptions.allGroups'),
            type: ReportFilterType.MultiSelect,
            options: groups.map(g => ({
                label: g.name,
                value: g.id
            }))
        },
        {
            key: "tagIds",
            label: t('reports.filterOptions.tags'),
            placeholder: t('reports.filterOptions.allTags'),
            type: ReportFilterType.MultiSelect,
            options: tags.map(t => ({
                label: t.name,
                value: t.id
            }))
        }
    ], [groups, tags, t]);

    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId) return;

        const load = async () => {
            const { groups, tags } = await ReportDataUtil.loadGroupsAndTags(orgId);
            setGroups(groups);
            setTags(tags);
        };

        load();
    }, [state.organization?.id]);

    useEffect(() => {
        if (!reportData) return;

        const filtered = applyFilters(reportData, filters, searchQuery);

        setFilteredReportData(filtered);
    }, [filters, reportData, searchQuery]);

    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId) return;

        // Clear existing data immediately
        setReportData(null);
        setFilteredReportData(null);

        // reset UI state
        setFilters(DEFAULT_FILTERS);
        setSearchQuery("");

        const loadData = async () => {

            const data = await ReportDataUtil.grabInitialDynamicWellnessSummaryData(orgId);
            setReportData(data);
            setFilteredReportData(data);
        };

        loadData();
    }, [state.organization?.id]);

    useEffect(() => {
        if (reportData) {
            setIsLoading(false);
        }
        else {
            setIsLoading(true);
        }
    }, [reportData])

    const applyFilters = (
        reportData: ReportDynamicWellnessSummaryData,
        filters: FilterState,
        searchQuery: string
    ): ReportDynamicWellnessSummaryData => {
        return ReportDataUtil.filterInitialDynamicWellnessSummaryData(reportData, filters, searchQuery);
    };

    const handleExportCsv = () => {
        if (!filteredReportData) return;

        const csvString = ReportDataUtil.generateWellnessSummary({
            averageEngagement: filteredReportData.averageEngagement,
            sevenDayEngagement: filteredReportData.sevenDayEngagement,
            thirtyDayEngagement: filteredReportData.thirtyDayEngagement,
            averageDaysMissed: filteredReportData.averageDaysMissed,
            averageStreak: filteredReportData.averageStreak,
            activePrograms: filteredReportData.activePrograms,
            pendingRetest: filteredReportData.pendingRetest,
            enrolledUsers: filteredReportData.enrolledUsers,
            userData: filteredReportData.userData,
            organizationName: filteredReportData.organizationName,
            reportDate: DateTime.now().toISODate(), // TODO translate
            totalUsers: filteredReportData.totalUsers,
            groupNames: filteredReportData.groupNames,
            tagNames: filteredReportData.tagNames,
        });

        const blob = new Blob(
            ["\uFEFF" + csvString], // UTF-8 BOM for Excel
            { type: "text/csv;charset=utf-8;" }
        );

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${t('reports.wellnessSummary.fileReportName')}_${DateTime.now().toISODate()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    return (
        <div className={COMPONENT_CLASS}>
            <Loader isVisible={isLoading} />
            <div className={`${COMPONENT_CLASS}__header-container`}>
                <div className={`${COMPONENT_CLASS}__header`}>
                    <h1>{t('reportType.wellness_summary_rep')}</h1>
                    <p className={`label-large ${COMPONENT_CLASS}__sub-header`}>{state.organization?.name} • {filteredReportData?.usersAndWellnessPrograms.length} {t('general.users')}</p>
                </div>
                <div className={`${COMPONENT_CLASS}__actions`}>
                    <Button
                        onClick={handleExportCsv}
                        buttonText={t('buttons.btn_exportCSV')}
                        type="link" />
                    <Button
                        disabled={!filteredReportData}
                        onClick={() => ReportDataUtil.downloadWellnessSummary(filteredReportData!, ReportDataUtil.buildReportFilterPayload(groups, tags, filters), state.organization?.id!, state.user?.id!)}
                        buttonText={t('buttons.btn_exportPDF')}
                        type="link" />
                    <ReportFilterMenu
                        filters={filterDefinitions}
                        values={filters}
                        onChange={(key, value) =>
                            setFilters(prev => ({
                                ...prev,
                                [key]: value
                            }))
                        }
                    >

                    </ReportFilterMenu>
                </div>
            </div>

            <div className={`${COMPONENT_CLASS}__summary`}>
                <div className={`${COMPONENT_CLASS}__summary-items`}>


                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>
                            {filteredReportData?.enrolledUsers}
                        </span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}>
                            {t('reports.wellnessSummary.engagement.enrolledUsers')}
                        </span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>
                            {filteredReportData?.activePrograms}
                        </span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}>
                            {t('reports.wellnessSummary.engagement.activePrograms')}
                        </span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>
                            {filteredReportData?.pendingRetest}
                        </span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}>
                            {t('reports.wellnessSummary.engagement.pendingRetest')}
                        </span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>
                            {filteredReportData?.averageDaysMissed}
                        </span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}>
                            {t('reports.wellnessSummary.engagement.averageDaysMissed')}
                        </span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>
                            {filteredReportData?.averageEngagement}%
                        </span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}>
                            {t('reports.wellnessSummary.engagement.averageEngagement')}
                        </span>
                    </div>

                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>
                            {filteredReportData?.sevenDayEngagement}%
                        </span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}>
                            {t('reports.wellnessSummary.engagement.sevenDayEngagement')}
                        </span>
                    </div>

                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>
                            {filteredReportData?.thirtyDayEngagement}%
                        </span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}>
                            {t('reports.wellnessSummary.engagement.thirtyDayEngagement')}
                        </span>
                    </div>
                    <div className={`${COMPONENT_CLASS}__summary-item`}>
                        <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>
                            {filteredReportData?.averageStreak}
                        </span>
                        <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}>
                            {t('reports.wellnessSummary.engagement.averageStreak')}
                        </span>
                    </div>
                </div>
            </div>

            {filteredReportData?.userData && (
                <div className={`${COMPONENT_CLASS}__table-container`}>
                    <div className={`${COMPONENT_CLASS}__table-header`}>
                        <h2>{t('reports.wellnessSummary.individualEngagement')}</h2>
                        <div className={`${COMPONENT_CLASS}__search-container`}>
                            <input
                                className={`input-field ${COMPONENT_CLASS}__search-input`}
                                type="text"
                                placeholder={t('reports.filterOptions.searchByName')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                    <ReportDynamicTable
                        rows={filteredReportData.userData}
                        columns={columns}
                        pageSize={PAGE_SIZE}
                    />
                </div>

            )}

        </div>
    );
}


export default ReportWellnessSummaryDynamic;
